import apiService from "../api/apiService";

export const PROMPT_TYPES = {
  SYSTEM: "system-prompt",
  USER: "user-prompt",
  INTERPRETATION: "interpretation-prompt",
};

// Default fallback prompts (only used if API fails)
const defaultPrompts = {
  system: `You are an AI assistant specialized in EPCIS data analysis.
You help users understand their supply chain data by answering questions and providing insights.
Always be clear, concise, and accurate in your responses.`,

  user: `I want you to analyze the EPCIS data and answer my questions.
Please provide specific examples from the data when relevant.
If you need any clarification, please ask.`,

  interpretation: `When interpreting EPCIS events:
1. Focus on the key business implications
2. Highlight any anomalies or patterns
3. Suggest potential optimizations
4. Consider the full context of the supply chain`,
};

// Track if we've initialized from backend
let initialized = false;

const promptService = {
  /**
   * Initialize prompts from backend API
   * Should be called on app startup
   */
  init: async () => {
    if (initialized) return;

    try {
      const response = await apiService.get("/prompts");
      const newPrompts = response.prompts || response;

      if (newPrompts && typeof newPrompts === "object" &&
          (newPrompts.system || newPrompts[PROMPT_TYPES.SYSTEM])) {

        const systemPrompt = newPrompts.system || newPrompts[PROMPT_TYPES.SYSTEM];
        const userPrompt = newPrompts.user || newPrompts[PROMPT_TYPES.USER] || defaultPrompts.user;
        const interpretationPrompt = newPrompts.interpretation ||
          newPrompts[PROMPT_TYPES.INTERPRETATION] || defaultPrompts.interpretation;

        // Store in localStorage for session use
        localStorage.setItem(PROMPT_TYPES.SYSTEM, systemPrompt);
        localStorage.setItem(PROMPT_TYPES.USER, userPrompt);
        localStorage.setItem(PROMPT_TYPES.INTERPRETATION, interpretationPrompt);

        console.log('[PromptService] Loaded prompts from backend');
      }
    } catch (error) {
      console.warn('[PromptService] Failed to load prompts from backend, using defaults:', error.message);
      // Use defaults if API fails and nothing in localStorage
      if (!localStorage.getItem(PROMPT_TYPES.SYSTEM)) {
        localStorage.setItem(PROMPT_TYPES.SYSTEM, defaultPrompts.system);
        localStorage.setItem(PROMPT_TYPES.USER, defaultPrompts.user);
        localStorage.setItem(PROMPT_TYPES.INTERPRETATION, defaultPrompts.interpretation);
      }
    }

    initialized = true;
  },

  getSystemPrompt: () =>
    localStorage.getItem(PROMPT_TYPES.SYSTEM) || defaultPrompts.system,

  getUserPrompt: () =>
    localStorage.getItem(PROMPT_TYPES.USER) || defaultPrompts.user,

  getInterpretationPrompt: () =>
    localStorage.getItem(PROMPT_TYPES.INTERPRETATION) || defaultPrompts.interpretation,

  getAllPrompts: () => ({
    [PROMPT_TYPES.SYSTEM]:
      localStorage.getItem(PROMPT_TYPES.SYSTEM) || defaultPrompts.system,
    [PROMPT_TYPES.USER]:
      localStorage.getItem(PROMPT_TYPES.USER) || defaultPrompts.user,
    [PROMPT_TYPES.INTERPRETATION]:
      localStorage.getItem(PROMPT_TYPES.INTERPRETATION) || defaultPrompts.interpretation,
  }),

  setPrompt: (type, value) => {
    localStorage.setItem(type, value);
  },

  /**
   * Reset prompts by fetching fresh from backend
   */
  resetPrompts: async () => {
    try {
      const response = await apiService.get("/prompts");
      const newPrompts = response.prompts || response;

      if (newPrompts && typeof newPrompts === "object" &&
          (newPrompts.system || newPrompts[PROMPT_TYPES.SYSTEM])) {

        const systemPrompt = newPrompts.system || newPrompts[PROMPT_TYPES.SYSTEM];
        const userPrompt = newPrompts.user || newPrompts[PROMPT_TYPES.USER] || defaultPrompts.user;
        const interpretationPrompt = newPrompts.interpretation ||
          newPrompts[PROMPT_TYPES.INTERPRETATION] || defaultPrompts.interpretation;

        localStorage.setItem(PROMPT_TYPES.SYSTEM, systemPrompt);
        localStorage.setItem(PROMPT_TYPES.USER, userPrompt);
        localStorage.setItem(PROMPT_TYPES.INTERPRETATION, interpretationPrompt);

        return {
          [PROMPT_TYPES.SYSTEM]: systemPrompt,
          [PROMPT_TYPES.USER]: userPrompt,
          [PROMPT_TYPES.INTERPRETATION]: interpretationPrompt,
        };
      } else {
        throw new Error("Invalid prompts data format from API");
      }
    } catch (apiError) {
      // If API fails, reset to default prompts
      localStorage.setItem(PROMPT_TYPES.SYSTEM, defaultPrompts.system);
      localStorage.setItem(PROMPT_TYPES.USER, defaultPrompts.user);
      localStorage.setItem(PROMPT_TYPES.INTERPRETATION, defaultPrompts.interpretation);

      return {
        [PROMPT_TYPES.SYSTEM]: defaultPrompts.system,
        [PROMPT_TYPES.USER]: defaultPrompts.user,
        [PROMPT_TYPES.INTERPRETATION]: defaultPrompts.interpretation,
      };
    }
  },
};

export default promptService;
